/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EditIntegrations.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { HandleApiActions } from "../../redux/actions";
import integrationConfigAction from "../../redux/actions/integrationConfigAction";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import JiraIcon from "../../assets/Jira.png";
import XrayIcon from "../../assets/PikPng.com_xray-png_5613333.png";
import ServiceNowIcon from "../../assets/ServiceNow_idec6JyuYi_1.png";
import { toast } from "react-toastify";

// Helper function to convert text to title case
const toTitleCase = (str) => {
  return str
    .split(/[\s_-]+/) // Split by space, underscore, or hyphen
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

function EditIntegrations({
  open,
  onClose,
  selectedItem,
  integrateSelect,
  fetchIntegrationDetails,
}) {
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState(selectedItem?.config || {});
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState(
    selectedItem?.config || {},
  );

  // Available pages/modules
  const availablePages = [
    { value: "PROJECT CONFIGURATOR", label: "Project Configurator" },
    { value: "VIRTUAL LAB-CAR", label: "Virtual Labcar" },
    { value: "LEGAL REQUIREMENTS", label: "Legal Requirements" },
    { value: "TEST CASES", label: "Test Cases" },
  ];

  const getToolIcon = (toolName) => {
    const iconMap = {
      JIRA: JiraIcon,
      "JIRA Software": JiraIcon,
      "JIRA Xray": XrayIcon,
      "JIRA Xray Test Case Management": XrayIcon,
      ServiceNow: ServiceNowIcon,
    };

    // Check exact match first
    let result = iconMap[toolName];

    // Fallback logic for tool name variations
    if (!result && toolName) {
      const lowerToolName = toolName.toLowerCase();
      if (lowerToolName.includes("jira") || lowerToolName.includes("xray")) {
        result = lowerToolName.includes("xray") ? XrayIcon : JiraIcon;
      } else if (
        lowerToolName.includes("service") &&
        lowerToolName.includes("now")
      ) {
        result = ServiceNowIcon;
      } else if (
        lowerToolName.includes("servicenow") ||
        lowerToolName.includes("snow")
      ) {
        result = ServiceNowIcon;
      }
    }

    return result || null;
  };

  // Reset form when dialog opens or selectedItem changes
  useEffect(() => {
    if (open && selectedItem) {
      const config = selectedItem.config || {};
      setFormValues(config);
      setInitialFormValues(config);
      setFormErrors({});
      setShowPassword(false);
    }
  }, [open, selectedItem]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };



  const handleTestAndSave = async () => {

    // Step 1: Test the connection
    const testReqObj = {
      id: parseInt(selectedItem.id),
      type: selectedItem?.connector_type?.toLowerCase(),
      auth: formValues?.auth,
      options: formValues?.options,
    };

    const testResp = await dispatch(
      HandleApiActions({
        ...integrationConfigAction.test_connectors,
        params: testReqObj,
        show_toast: false, // we'll manage manually
      }),
    );

    // Step 2: Handle each case clearly
    if (testResp?.ok === true && testResp?.isConnected === false) {
      // Case: Connection failed, need to update integration to set status false (to reconnect)
      const updateResp = await dispatch(
        HandleApiActions({
          ...integrationConfigAction.update_integrations,
          params: {
            id: selectedItem?.id,
            status: false, // Mark it disconnected to trigger reconnection flow
          },
          show_toast: false,
        }),
      );

      if (!updateResp?.error) {
        fetchIntegrationDetails();
        toast.warning(
          testResp?.message ||
          "Connection test failed. Integration status updated to disconnected.",
        );
      } else {
        toast.error(
          "Connection failed and updating integration status also failed.",
        );
      }

      onClose();
    } else if (testResp?.ok === true && testResp?.isConnected === true) {
      // Case: Connection successful, now save config
      const saveReqObj = {
        id: selectedItem.id,
        auth: formValues?.auth,
        options: formValues?.options,
        encryptPassword:
          selectedItem?.module_type.toLowerCase() !== "intersection",
      };

      const saveResp = await dispatch(
        HandleApiActions({
          ...integrationConfigAction.save_integrations,
          params: saveReqObj,
          show_toast: false,
        }),
      );

      if (!saveResp?.error) {
        fetchIntegrationDetails();
        toast.success(
          testResp?.message ||
          "Connection tested successfully and configuration saved!",
        );
      } else {
        toast.error("Connection successful but failed to save configuration.");
      }

      onClose();
    } else {
      // Case: Connection test failed completely (ok == false)
      toast.error(
        // testResp?.message ||
        "Connection test failed. Please check your configuration.",
      );
    }
  };

  const handleDisconnect = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...integrationConfigAction.update_integrations,
        params: {
          id: selectedItem?.id,
          status: false,
        },
        show_toast: true,
      }),
    );
    if (!resp?.error) {
      fetchIntegrationDetails();
      onClose();
    }
  };

  const hasFormChanged = () => {
    return JSON.stringify(formValues) !== JSON.stringify(initialFormValues);
  };

  const isFormValid = () => {
    if (!integrateSelect) return false;

    const authValid = integrateSelect?.auth?.fields?.every(
      (field) => !field.required || formValues.auth?.[field.key]?.trim(),
    );

    const optionsValid = integrateSelect?.options?.every(
      (opt) => !opt.required || formValues.options?.[opt.key]?.trim(),
    );

    return authValid && optionsValid && hasFormChanged();
  };

  if (!selectedItem || !integrateSelect) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          minHeight: "500px",
          height: "500px",
          borderRadius: 4,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6">Update Integration</Typography>
          {/* <Chip
            label={selectedItem.is_connected ? "Connected" : "Disconnected"}
            color={selectedItem.is_connected ? "success" : "default"}
            size="small"
          /> */}
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          overflow: "auto",
          // Custom scrollbar styling
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#c1c1c1",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#a8a8a8",
          },
        }}
      >
        {/* Integration Type Dropdown - Disabled */}
        <CustomSelectV2
          label="Select Tool"
          required={true}
          optionsList={[
            {
              label: integrateSelect?.name,
              value: selectedItem?.connector_type,
              icon: getToolIcon(integrateSelect?.name),
            },
          ]}
          selectedValue={selectedItem?.connector_type}
          handleOnChange={() => { }} // No-op since it's disabled
          disabled={true}
          sltFldStyle={{ mb: 0 }}
        />

        {/* Page Selection Dropdown - Disabled */}
        <CustomSelectV2
          label="Select Page"
          required={true}
          optionsList={availablePages}
          selectedValue={selectedItem?.module_type}
          handleOnChange={() => { }} // No-op since it's disabled
          disabled={true}
          sltFldStyle={{ mb: 0 }}
        />

        {/* Authentication Fields */}
        <Box>
          {/* <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
            Authentication
          </Typography> */}
          {integrateSelect?.auth?.fields?.map((field) => (
            <CustomFormTextFieldV2
              key={field.key}
              size="small"
              label={toTitleCase(field.label)}
              type={
                field.type === "password" && !showPassword ? "password" : "text"
              }
              placeholder={field.label}
              required={field.required}
              value={formValues.auth?.[field.key] || ""}
              setValue={(value) => {
                setFormValues((prev) => ({
                  ...prev,
                  auth: {
                    ...prev.auth,
                    [field.key]: value,
                  },
                }));
              }}
              valueError={formErrors.auth?.[field.key] || ""}
              setValueError={(error) => {
                setFormErrors((prev) => ({
                  ...prev,
                  auth: {
                    ...prev.auth,
                    [field.key]: error,
                  },
                }));
              }}
              variant="outlined"
              txtFldStyle={{ mb: 2 }}
              inputProps={
                field.type === "password"
                  ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={handleTogglePassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                  : {}
              }
            />
          ))}
        </Box>

        {/* Options Fields */}
        {integrateSelect?.options && integrateSelect.options.length > 0 && (
          <Box>
            {/* <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Options
            </Typography> */}
            {integrateSelect.options.map((option) => (
              <CustomFormTextFieldV2
                key={option.key}
                size="small"
                label={toTitleCase(option.label)}
                type="text"
                placeholder={option.label}
                required={option.required}
                value={formValues.options?.[option.key] || ""}
                setValue={(value) => {
                  setFormValues((prev) => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      [option.key]: value,
                    },
                  }));
                }}
                valueError={formErrors.options?.[option.key] || ""}
                setValueError={(error) => {
                  setFormErrors((prev) => ({
                    ...prev,
                    options: {
                      ...prev.options,
                      [option.key]: error,
                    },
                  }));
                }}
                variant="outlined"
                txtFldStyle={{ mb: 2 }}
              />
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{ display: "flex", justifyContent: "space-between", p: 2, gap: 1 }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
          {selectedItem.is_connected ? (
            <CustomButtonV2
              handleOnClick={handleDisconnect}
              type="cancel"
              btnName="Disconnect"
            />
          ) : (
            <CustomButtonV2
              handleOnClick={handleTestAndSave}
              // disabled={!selectedItem?.is_connected}
              type="cancel"
              btnName="Connect"
            />
          )}
          <CustomButtonV2
            handleOnClick={onClose}
            type="cancel"
            btnName="Cancel"
          />
        </Box>
        <Box>
          <CustomButtonV2
            handleOnClick={handleTestAndSave}
            disabled={!isFormValid()}
            type="submit"
            btnName="Test Connect & Update"
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditIntegrations;
